/** @jsx jsx */
import { createContext, FC, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { isCancelledError } from "react-query";
import { useDialogState } from "reakit/Dialog";
import { jsx } from "theme-ui";

import {
  TimeReport,
  TimeReportProjectItemPath,
  TransformTimeReportProjectItemFunction,
  getTimeReportProjectItem,
  transformTimeReportProjectItem,
} from "@/data/time-reports";
import { useTimeReportMutations } from "@/data/time-reports/queries/useTimeReportMutations";
import { throwError } from "@/utils/functions";

import { TimeReportSpecificationModal } from "../specification/TimeReportSpecificationModal";
import {
  timeReportEditInitialState,
  TimeReportEditItemStatus,
  timeReportEditReducer,
} from "./_components/timeReportEditReducer";
import { NewTimeReportVersionModal } from "./NewTimeReportVersionModal";

export const MODIFIED_BY_ANOTHER_USER_ERROR_CODE = "MODIFIED_BY_ANOTHER_USER";

export interface TimeReportEditContextValue {
  itemsStatus: Record<string, TimeReportEditItemStatus>;
  working: boolean;

  updateTimeReport: (newTimeReport: TimeReport) => Promise<void>;
  updateItem: (path: TimeReportProjectItemPath, transform: TransformTimeReportProjectItemFunction) => Promise<void>;
  updateCalendarItem: (path: TimeReportProjectItemPath, transform: TransformTimeReportProjectItemFunction) => void;

  showSpecificationModal: (itemPath: TimeReportProjectItemPath) => void;
}

export const TimeReportEditContext = createContext<TimeReportEditContextValue>({
  itemsStatus: {},
  working: false,
  updateTimeReport: throwError,
  updateItem: throwError,
  updateCalendarItem: throwError,
  showSpecificationModal: throwError,
});

export const useTimeReportEdit = () => useContext(TimeReportEditContext);

interface TimeReportEditProviderProps {
  timeReport: TimeReport;
  refetchTimeReport: () => Promise<unknown>;
}

export const TimeReportEditProvider: FC<TimeReportEditProviderProps> = ({
  timeReport,
  refetchTimeReport,
  children,
}) => {
  const { update, updateProjectItem } = useTimeReportMutations(timeReport.guid);

  const timeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});

  const [state, dispatch] = useReducer(timeReportEditReducer, timeReportEditInitialState);

  const working = useMemo(() => Object.values(state.itemsStatus).some(s => s === "working"), [state.itemsStatus]);

  const [specificationModalItemPath, setSpecificationModalItemPath] = useState<TimeReportProjectItemPath>();
  const specificationModalState = useDialogState({ animated: true });

  const newTimeReportVersionModalState = useDialogState({ animated: true });

  const showSpecificationModal = useCallback(
    (itemPath: TimeReportProjectItemPath) => {
      setSpecificationModalItemPath(itemPath);
      specificationModalState.show();
    },
    [specificationModalState],
  );

  const updateTimeReport = useCallback(
    async (newTimeReport: TimeReport) => {
      try {
        await update(newTimeReport);
      } catch (error) {
        if (error?.payload?.code === MODIFIED_BY_ANOTHER_USER_ERROR_CODE) {
          newTimeReportVersionModalState.show();
        } else {
          throw error;
        }
      }
    },
    [update, newTimeReportVersionModalState],
  );

  const updateItem = useCallback(
    async (path: TimeReportProjectItemPath, transform: TransformTimeReportProjectItemFunction) => {
      try {
        const newTimeReport = transformTimeReportProjectItem(timeReport, path, transform);

        await updateProjectItem({
          path,
          newTimeReport,
          newItem: getTimeReportProjectItem(newTimeReport, path)!,
        });
      } catch (error) {
        if (error?.payload?.code === MODIFIED_BY_ANOTHER_USER_ERROR_CODE) {
          newTimeReportVersionModalState.show();
        } else {
          throw error;
        }
      }
    },
    [timeReport, updateProjectItem, newTimeReportVersionModalState],
  );

  const updateCalendarItem = useCallback(
    async (path: TimeReportProjectItemPath, transform: TransformTimeReportProjectItemFunction) => {
      dispatch({ type: "ENQUEUE_UPDATE", path, transform });
    },
    [],
  );

  const clearItemStatus = useCallback((pathId: string) => {
    dispatch({ type: "CLEAR_ITEM_STATUS", pathId });
    delete timeoutsRef.current[pathId];
  }, []);

  useEffect(() => {
    const updateNextItem = async () => {
      if (!state.updatingItemPath && state.queue.length > 0) {
        const queueItem = state.queue[0];
        dispatch({ type: "DEQUEUE_UPDATE" });

        try {
          const newTimeReport = transformTimeReportProjectItem(timeReport, queueItem.path, queueItem.transform);

          await updateProjectItem({
            path: queueItem.path,
            newTimeReport,
            newItem: getTimeReportProjectItem(newTimeReport, queueItem.path)!,
          });

          dispatch({ type: "CLEAR_UPDATING_ITEM", outcome: "success" });
        } catch (error) {
          if (error?.payload?.code === MODIFIED_BY_ANOTHER_USER_ERROR_CODE) {
            newTimeReportVersionModalState.show();
            dispatch({ type: "CLEAR_QUEUE" });
          } else if (!isCancelledError(error)) {
            dispatch({ type: "CLEAR_UPDATING_ITEM", outcome: "error" });
          }
        }

        timeoutsRef.current[queueItem.pathId] = setTimeout(() => clearItemStatus(queueItem.pathId), 750);
      }
    };

    updateNextItem();
  }, [state, timeReport, updateProjectItem, clearItemStatus, newTimeReportVersionModalState]);

  useEffect(() => {
    const timeouts = timeoutsRef.current;

    return () => {
      Object.values(timeouts).forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  const contextValue: TimeReportEditContextValue = {
    itemsStatus: state.itemsStatus,
    working,
    updateTimeReport,
    updateItem,
    updateCalendarItem,
    showSpecificationModal,
  };

  return (
    <TimeReportEditContext.Provider value={contextValue}>
      {children}

      {specificationModalItemPath && (
        <TimeReportSpecificationModal
          dialogState={specificationModalState}
          timeReport={timeReport}
          itemPath={specificationModalItemPath}
        />
      )}

      {newTimeReportVersionModalState.visible && (
        <NewTimeReportVersionModal dialogState={newTimeReportVersionModalState} refetchTimeReport={refetchTimeReport} />
      )}
    </TimeReportEditContext.Provider>
  );
};
